import { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, ArrowLeft, Zap } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import { useSEO } from "@/hooks/useSEO";
import Breadcrumbs from "@/components/ui/breadcrumbs";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);

  const jsonLd = useMemo(() => post ? [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      author: { "@type": "Organization", name: post.author },
      datePublished: post.date,
      publisher: { "@type": "Organization", name: "eScooter", logo: { "@type": "ImageObject", url: "/favicon.ico" } },
      mainEntityOfPage: `https://patineteelectricosegundamano.com/blog/${post.slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: "https://patineteelectricosegundamano.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://patineteelectricosegundamano.com/blog" },
        { "@type": "ListItem", position: 3, name: post.title, item: `https://patineteelectricosegundamano.com/blog/${post.slug}` },
      ],
    },
  ] : undefined, [post]);

  useSEO({
    title: post ? `${post.title} | eScooter Blog` : "Blog eScooter",
    description: post?.excerpt ?? "Blog sobre patinetes eléctricos",
    canonical: post ? `/blog/${post.slug}` : "/blog",
    type: "article",
    image: post?.image,
    jsonLd,
  });

  if (!post) return <Navigate to="/blog" replace />;

  // Simple markdown-ish renderer for headings, paragraphs, lists, tables, bold
  const renderContent = (content: string) => {
    const lines = content.trim().split("\n");
    const elements: JSX.Element[] = [];
    let tableRows: string[][] = [];
    let inTable = false;

    const processInline = (text: string) =>
      text.split(/(\*\*.*?\*\*)/).map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
          part
        )
      );

    const flushTable = () => {
      if (tableRows.length < 2) return;
      const headers = tableRows[0];
      const dataRows = tableRows.slice(2); // skip separator row
      elements.push(
        <div key={`table-${elements.length}`} className="my-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {headers.map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-semibold">{h.trim()}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, ri) => (
                <tr key={ri} className="border-b border-border/50">
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-2 text-muted-foreground">{cell.trim()}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
      inTable = false;
    };

    let ulItems: JSX.Element[] = [];
    let olItems: JSX.Element[] = [];

    const flushUl = () => {
      if (ulItems.length) {
        elements.push(<ul key={`ul-${elements.length}`} className="ml-5 list-disc space-y-1 text-muted-foreground leading-relaxed">{ulItems}</ul>);
        ulItems = [];
      }
    };
    const flushOl = () => {
      if (olItems.length) {
        elements.push(<ol key={`ol-${elements.length}`} className="ml-5 list-decimal space-y-1 text-muted-foreground leading-relaxed">{olItems}</ol>);
        olItems = [];
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.includes("|") && line.trim().startsWith("|")) {
        flushUl(); flushOl();
        inTable = true;
        const cells = line.split("|").filter(Boolean);
        tableRows.push(cells);
        continue;
      } else if (inTable) {
        flushTable();
      }

      if (!line.trim()) continue;

      if (line.startsWith("## ")) {
        flushUl(); flushOl();
        elements.push(<h2 key={i} className="mt-8 mb-3 font-display text-2xl font-bold">{line.slice(3)}</h2>);
      } else if (line.startsWith("### ")) {
        flushUl(); flushOl();
        elements.push(<h3 key={i} className="mt-6 mb-2 font-display text-xl font-semibold">{line.slice(4)}</h3>);
      } else if (line.startsWith("- ")) {
        flushOl();
        ulItems.push(<li key={i}>{processInline(line.slice(2))}</li>);
      } else if (/^\d+\.\s/.test(line)) {
        flushUl();
        olItems.push(<li key={i}>{processInline(line.replace(/^\d+\.\s/, ""))}</li>);
      } else {
        flushUl(); flushOl();
        elements.push(
          <p key={i} className="my-3 leading-relaxed text-muted-foreground">
            {processInline(line)}
          </p>
        );
      }
    }

    if (inTable) flushTable();
    flushUl(); flushOl();
    return elements;
  };

  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <Layout>
      <article className="py-10">
        <div className="container max-w-3xl">
          <Breadcrumbs
            className="mb-4"
            items={[{ label: "Blog", href: "/blog" }, { label: post.title }]}
          />
          <Link to="/blog" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Volver al blog
          </Link>

          <header className="mb-8">
            <div className="mb-3 flex items-center gap-3">
              <Badge variant="secondary">{post.category}</Badge>
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" />
                {new Date(post.date).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
              </span>
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5" /> {post.readTime}
              </span>
            </div>
            <h1 className="font-display text-3xl font-bold leading-tight md:text-4xl">{post.title}</h1>
            <p className="mt-3 text-lg text-muted-foreground">{post.excerpt}</p>
          </header>

          <div className="aspect-video overflow-hidden rounded-xl">
            <img src={post.image} alt={post.title} className="h-full w-full object-cover" />
          </div>

          <div className="mt-8">{renderContent(post.content)}</div>

          {/* CTA */}
          <div className="mt-12 rounded-xl border border-primary/20 bg-primary/5 p-6 text-center">
            <Zap className="mx-auto mb-2 h-8 w-8 text-primary" />
            <h3 className="font-display text-xl font-bold">¿Buscas un patinete eléctrico?</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Encuentra las mejores ofertas en nuestro marketplace especializado.
            </p>
            <div className="mt-4 flex justify-center gap-3">
              <Button asChild><Link to="/buscar">Explorar patinetes</Link></Button>
              <Button variant="outline" asChild><Link to="/publicar">Vender el mío</Link></Button>
            </div>
          </div>

          {/* Related */}
          {otherPosts.length > 0 && (
            <div className="mt-12">
              <h3 className="mb-4 font-display text-xl font-bold">También te puede interesar</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {otherPosts.map((p) => (
                  <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-lg border border-border p-4 transition-shadow hover:shadow-md">
                    <Badge variant="outline" className="mb-2 text-xs">{p.category}</Badge>
                    <h4 className="font-display text-sm font-semibold leading-snug group-hover:text-primary">{p.title}</h4>
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{p.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </Layout>
  );
};

export default BlogPost;
